import type { JSX } from "react";
import { useEffect, useState } from "react";
import { renderMarp, slidesToPptxBlob, slidesToStandaloneHtml, downloadBlob } from "./slides";
import { PAGER_JS, DECK_CSS, PAGER_HTML } from "./deck-pager";

/**
 * Artifact Deck view (P3 · 演示 mode). Renders the artifact's markdown as Marp
 * slides inside a SANDBOXED iframe (allow-scripts only — no same-origin, no forms),
 * which both isolates Marp's global CSS from the app AND contains the owner-authored
 * markdown safely. A tiny injected pager shows one slide at a time (‹ n/total ›,
 * arrow keys). Export is client-side + on-demand (nothing binary is stored):
 * 导出 PPTX → native editable pptxgenjs deck, 导出 HTML → standalone Marp document.
 * The artifact itself stays markdown, so grounding/edit/version are unaffected.
 */
export function ArtifactDeck({ markdown, title }: { markdown: string; title: string }): JSX.Element {
  const [srcDoc, setSrcDoc] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState<"pptx" | "html" | null>(null);

  useEffect(() => {
    let alive = true;
    setSrcDoc(null);
    setErr(null);
    renderMarp(markdown)
      .then(({ html, css }) => {
        if (alive) setSrcDoc(buildDeckSrcDoc(html, css));
      })
      .catch(() => {
        if (alive) setErr("演示渲染失败，请稍后再试。");
      });
    return () => {
      alive = false;
    };
  }, [markdown]);

  async function onExport(kind: "pptx" | "html"): Promise<void> {
    setExportBusy(kind);
    setErr(null);
    try {
      const name = (title || "演示").replace(/[/\\?%*:|"<>]/g, "");
      if (kind === "pptx") {
        downloadBlob(
          await slidesToPptxBlob(markdown, title),
          `${name}.pptx`,
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        );
      } else {
        downloadBlob(await slidesToStandaloneHtml(markdown, title), `${name}.html`, "text/html");
      }
    } catch {
      setErr(kind === "pptx" ? "导出 PPTX 失败，请稍后再试。" : "导出 HTML 失败，请稍后再试。");
    }
    setExportBusy(null);
  }

  return (
    <div className="artifact-deck" data-testid="artifact-deck">
      <div className="artifact-deck-bar">
        <span className="artifact-deck-hint">演示模式 · 用 ← → 翻页</span>
        <div className="artifact-deck-acts">
          <button
            type="button"
            className="artifact-btn"
            disabled={exportBusy !== null}
            data-testid="artifact-deck-export-pptx"
            onClick={() => void onExport("pptx")}
          >
            {exportBusy === "pptx" ? "导出中…" : "导出 PPTX"}
          </button>
          <button
            type="button"
            className="artifact-btn"
            disabled={exportBusy !== null}
            data-testid="artifact-deck-export-html"
            onClick={() => void onExport("html")}
          >
            {exportBusy === "html" ? "导出中…" : "导出 HTML"}
          </button>
        </div>
      </div>
      {err !== null && (
        <div className="artifact-canvas-note" role="alert">
          {err}
        </div>
      )}
      {srcDoc === null ? (
        <div className="artifact-canvas-note">渲染中…</div>
      ) : (
        <iframe
          className="artifact-deck-frame"
          title="演示"
          sandbox="allow-scripts"
          srcDoc={srcDoc}
          data-testid="artifact-deck-frame"
        />
      )}
    </div>
  );
}

/** Assemble the sandboxed deck document (Marp html/css + pager). */
function buildDeckSrcDoc(html: string, css: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><style>${css}${DECK_CSS}</style></head><body>${html}${PAGER_HTML}<script>${PAGER_JS}</script></body></html>`;
}
